import React from 'react';
import { Typography } from '@attentive-platform/stem-ui';
import {
  BannerBox,
  BannerIcon,
  SwitchConfirmBox,
  CondBlock,
  FieldWrap,
  FieldLabel,
  HelpNote,
  TierTableWrap,
  TwoColRow
} from './styles';

/** Same as Constants `BILLING_FREQUENCY_ENUM` / Django BillingFrequency — local copy avoids circular import */
const BILLING_FREQUENCY_ENUM = { QUARTERLY: 4, MONTHLY: 12, HALF_YEARLY: 2, ANNUAL: 1 };
const CATEGORY_PRICING_TYPES = { FRESH: 'fresh', ADDENDUM: 'addendum' };

const BILLING_FREQUENCY_OPTIONS = [
  { label: 'Quarterly', value: BILLING_FREQUENCY_ENUM.QUARTERLY },
  { label: 'Monthly', value: BILLING_FREQUENCY_ENUM.MONTHLY },
  { label: 'Half Yearly', value: BILLING_FREQUENCY_ENUM.HALF_YEARLY },
  { label: 'Annual', value: BILLING_FREQUENCY_ENUM.ANNUAL }
];

const Banner = ({ variant, children }) => (
  <BannerBox variant={variant}>
    <BannerIcon>{variant === 'info' ? 'i' : '!'}</BannerIcon>
    <div>{children}</div>
  </BannerBox>
);

const MultiTradeSection = ({
  dfyModel,
  showSwitchConfirm,
  setShowSwitchConfirm,
  handleSwitchConfirm,
  dfyJobCap,
  setDfyJobCap,
  tierSchedule,
  setTierSchedule,
  showTierEditor,
  setShowTierEditor,
  addTierRow,
  updateTierRow,
  removeTierRow,
  contractPricing,
  setContractPricing,
  categories,
  selectedCategories,
  handleCategorySelect,
  handleCategoryPricingChange,
  handleDeleteCategory
}) => (
  <>
    {showSwitchConfirm && dfyModel === 'usage_based' && (
      <SwitchConfirmBox>
        <Typography variant='body3' style={{ marginBottom: '10px' }}>
          Switching this company from <strong>usage-based → license-based</strong> DFY. This is one-way in v1.
        </Typography>
        <div style={{ fontSize: '12.5px', fontWeight: 600, marginBottom: '6px' }}>What changes immediately on save:</div>
        <ul style={{ fontSize: '12.5px', paddingLeft: '18px', lineHeight: 1.7, marginBottom: '10px' }}>
          <li>Customer's Settings → Usage page is hidden.</li>
          <li>Hard-blocking enabled — new DFY submissions rejected when remaining ≤ 0.</li>
          <li>BPT contract amount, sheet costs, billing frequency, and Categories / Trade-wise pricing are hidden.</li>
          <li>DFY Job Cap becomes required. Enter it below before saving.</li>
          <li>Tier Schedule becomes optional (defaults to 1 unit/job if not set).</li>
        </ul>
        <Banner variant='error'>
          One-way in v1. Reverting requires engineering. Confirm the customer has agreed to license-based terms.
        </Banner>
        <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
          <button
            type='button'
            className='btn btn-outline-secondary btn-sm'
            onClick={() => setShowSwitchConfirm(false)}>
            Cancel switch
          </button>
          <button type='button' className='btn btn-danger btn-sm' onClick={handleSwitchConfirm}>
            Confirm — switch to license-based
          </button>
        </div>
      </SwitchConfirmBox>
    )}

    {dfyModel === 'license_based' && (
      <CondBlock>
        <FieldWrap>
          <FieldLabel>
            DFY Job Cap <span style={{ color: '#e53e3e' }}>*</span>
          </FieldLabel>
          <input
            type='number'
            className='form-control form-control-sm'
            style={{ maxWidth: '200px' }}
            value={dfyJobCap}
            min={0}
            onChange={e => setDfyJobCap(e.target.value)}
          />
          <HelpNote>
            Editing this updates the cap for the current period. Existing usage is preserved (only the cap changes).
          </HelpNote>
        </FieldWrap>

        <div style={{ marginBottom: '0.75rem' }}>
          <div
            style={{
              fontSize: '13px',
              fontWeight: 600,
              color: '#2d3748',
              marginBottom: '4px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}>
            DFY Tier Schedule
          </div>
          <Typography variant='body4' color='var(--color-neutral-60)' style={{ fontSize: '12.5px' }}>
            {tierSchedule.length > 0
              ? `Currently: ${tierSchedule.length} tier${tierSchedule.length !== 1 ? 's' : ''} configured.`
              : 'Currently: no tiers. Falls back to 1 unit per job.'}{' '}
            {!showTierEditor && 'If cleared, DFY falls back to 1 unit per job.'}
          </Typography>
          {!showTierEditor && (
            <button
              type='button'
              className='btn btn-outline-secondary btn-sm'
              style={{ marginTop: '6px' }}
              onClick={() => setShowTierEditor(true)}>
              Edit tier schedule
            </button>
          )}

          {showTierEditor && (
            <div style={{ marginTop: '10px' }}>
              <Typography
                variant='body4'
                color='var(--color-neutral-60)'
                style={{ marginBottom: '8px', fontSize: '12.5px' }}>
                Thresholds strictly increasing integers; units non-decreasing integers.
              </Typography>
              <TierTableWrap>
                <table>
                  <thead>
                    <tr>
                      <th style={{ width: '40%' }}>Max chargeable sheets ≤</th>
                      <th style={{ width: '40%' }}>Units consumed</th>
                      <th style={{ width: '80px' }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tierSchedule.map((row, index) => (
                      <tr key={index}>
                        <td>
                          <input
                            type='number'
                            value={row.max_sheets}
                            min={0}
                            onChange={e => updateTierRow(index, 'max_sheets', e.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            type='number'
                            value={row.units}
                            min={0}
                            onChange={e => updateTierRow(index, 'units', e.target.value)}
                          />
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <button
                            type='button'
                            className='btn btn-link btn-sm p-0'
                            style={{ color: '#e53e3e', fontSize: '12px' }}
                            onClick={() => removeTierRow(index)}>
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))}
                    <tr>
                      <td colSpan={3} style={{ textAlign: 'center', background: '#fafbfc' }}>
                        <button type='button' className='btn btn-outline-secondary btn-sm' onClick={addTierRow}>
                          + Add tier
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </TierTableWrap>
              <div style={{ marginTop: '10px', display: 'flex', gap: '8px' }}>
                <button
                  type='button'
                  className='btn btn-outline-secondary btn-sm'
                  onClick={() => {
                    setTierSchedule([]);
                    setShowTierEditor(false);
                  }}>
                  Clear all (revert to 1 unit/job)
                </button>
                <button type='button' className='btn btn-primary btn-sm' onClick={() => setShowTierEditor(false)}>
                  Apply tier schedule
                </button>
              </div>
            </div>
          )}
        </div>
      </CondBlock>
    )}

    {dfyModel === 'usage_based' && (
      <CondBlock>
        <div style={{ fontSize: '13px', fontWeight: 600, color: '#2d3748', marginBottom: '0.5rem' }}>DFY pricing</div>

        <FieldWrap>
          <FieldLabel>
            Contract Amount for BPT ($) <span style={{ color: '#e53e3e' }}>*</span>
          </FieldLabel>
          <input
            type='number'
            className='form-control form-control-sm'
            value={contractPricing.bpt_contract_amount}
            min={0}
            step={0.01}
            onChange={e => setContractPricing(prev => ({ ...prev, bpt_contract_amount: e.target.value }))}
          />
        </FieldWrap>

        <TwoColRow>
          <FieldWrap>
            <FieldLabel>
              Workable Sheet Cost BPT ($) <span style={{ color: '#e53e3e' }}>*</span>
            </FieldLabel>
            <input
              type='number'
              className='form-control form-control-sm'
              value={contractPricing.workable_sheet_cost}
              min={0}
              step={0.01}
              onChange={e => setContractPricing(prev => ({ ...prev, workable_sheet_cost: e.target.value }))}
            />
          </FieldWrap>
          <FieldWrap>
            <FieldLabel>
              Addendum Sheet Cost BPT ($) <span style={{ color: '#e53e3e' }}>*</span>
            </FieldLabel>
            <input
              type='number'
              className='form-control form-control-sm'
              value={contractPricing.addendum_sheet_cost}
              min={0}
              step={0.01}
              onChange={e => setContractPricing(prev => ({ ...prev, addendum_sheet_cost: e.target.value }))}
            />
          </FieldWrap>
        </TwoColRow>

        <FieldWrap>
          <FieldLabel>
            Workable Sheet in Manual Measurement for BPT ($) <span style={{ color: '#e53e3e' }}>*</span>
          </FieldLabel>
          <input
            type='number'
            className='form-control form-control-sm'
            value={contractPricing.manual_worksheet_cost}
            min={0}
            step={0.01}
            onChange={e => setContractPricing(prev => ({ ...prev, manual_worksheet_cost: e.target.value }))}
          />
        </FieldWrap>

        <FieldWrap>
          <FieldLabel>Billing Frequency</FieldLabel>
          <select
            className='form-select form-select-sm'
            style={{ maxWidth: '220px' }}
            value={contractPricing.billing_frequency}
            onChange={e => setContractPricing(prev => ({ ...prev, billing_frequency: Number(e.target.value) }))}>
            {BILLING_FREQUENCY_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </FieldWrap>

        <div
          style={{ fontSize: '13px', fontWeight: 600, color: '#2d3748', marginBottom: '0.5rem', marginTop: '0.75rem' }}>
          Categories / Trade-wise pricing
        </div>

        <FieldWrap>
          <FieldLabel>Select Categories</FieldLabel>
          <select
            className='form-select form-select-sm'
            style={{ maxWidth: '280px' }}
            value=''
            onChange={handleCategorySelect}>
            <option value=''>Select Categories</option>
            {categories
              .filter(cat => !selectedCategories.includes(String(cat.id)))
              .map(cat => (
                <option key={cat.id} value={String(cat.id)}>
                  {cat.name}
                </option>
              ))}
          </select>
        </FieldWrap>

        <TierTableWrap>
          <table>
            <thead>
              <tr>
                <th>Category</th>
                <th>Fresh ($)</th>
                <th>Addendum ($)</th>
                <th style={{ width: '70px' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <strong>Others</strong> <span style={{ fontSize: '11px', color: '#718096' }}>(required)</span>
                </td>
                <td>
                  <input
                    type='number'
                    placeholder='Fresh'
                    value={contractPricing.trade_wise_pricing?.others?.[CATEGORY_PRICING_TYPES.FRESH] ?? ''}
                    min={0}
                    step={0.01}
                    onChange={e => handleCategoryPricingChange('others', CATEGORY_PRICING_TYPES.FRESH, e.target.value)}
                  />
                </td>
                <td>
                  <input
                    type='number'
                    placeholder='Addendum'
                    value={contractPricing.trade_wise_pricing?.others?.[CATEGORY_PRICING_TYPES.ADDENDUM] ?? ''}
                    min={0}
                    step={0.01}
                    onChange={e =>
                      handleCategoryPricingChange('others', CATEGORY_PRICING_TYPES.ADDENDUM, e.target.value)
                    }
                  />
                </td>
                <td style={{ textAlign: 'center', color: '#718096' }}>—</td>
              </tr>
              {selectedCategories.map(catId => {
                const cat = categories.find(c => String(c.id) === catId);
                return (
                  <tr key={catId}>
                    <td>
                      <strong>{cat?.name || catId}</strong>
                    </td>
                    <td>
                      <input
                        type='number'
                        placeholder='Fresh'
                        value={contractPricing.trade_wise_pricing?.[catId]?.[CATEGORY_PRICING_TYPES.FRESH] ?? ''}
                        min={0}
                        step={0.01}
                        onChange={e => handleCategoryPricingChange(catId, CATEGORY_PRICING_TYPES.FRESH, e.target.value)}
                      />
                    </td>
                    <td>
                      <input
                        type='number'
                        placeholder='Addendum'
                        value={contractPricing.trade_wise_pricing?.[catId]?.[CATEGORY_PRICING_TYPES.ADDENDUM] ?? ''}
                        min={0}
                        step={0.01}
                        onChange={e =>
                          handleCategoryPricingChange(catId, CATEGORY_PRICING_TYPES.ADDENDUM, e.target.value)
                        }
                      />
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <button
                        type='button'
                        className='btn btn-link btn-sm p-0'
                        style={{ color: '#e53e3e', fontSize: '12px' }}
                        onClick={() => handleDeleteCategory(catId)}>
                        Remove
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </TierTableWrap>
      </CondBlock>
    )}
  </>
);

export default MultiTradeSection;
